import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { expireSession, refreshAccessToken } from '../../axios';
import {
  TOKEN_KEEPALIVE_MS,
  getLastActivity,
  getRemainingMs,
  getTokenRefreshedAt,
  isLoggedIn,
  markActivity,
  markTokenRefreshed,
} from './idleTimeout';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const CHECK_INTERVAL_MS = 1000;
// Mouse moves and scrolls fire constantly; one localStorage write a second is plenty.
const ACTIVITY_THROTTLE_MS = 1000;
// After a keepalive refresh fails for a non-auth reason (e.g. offline), wait
// this long before trying again instead of retrying on every click.
const KEEPALIVE_RETRY_MS = 30 * 1000;

// Logs the user out after IDLE_TIMEOUT_MS without a click, key press, scroll,
// touch or page change. Background API calls (polling) deliberately don't count.
const SessionTimeout = () => {
  const { pathname } = useLocation();
  const lastWriteRef = useRef(0);
  const lastKeepaliveAttemptRef = useRef(0);
  const previousPathnameRef = useRef(pathname);

  // The server-side session ends a fixed time after the last token refresh.
  // Refreshing on real activity keeps it alive while the user works in parts
  // of the UI that make no API calls, and lets it lapse once they go idle.
  const keepTokenAlive = (now) => {
    const refreshedAt = getTokenRefreshedAt();
    // Sessions started before this existed have no timestamp yet.
    if (refreshedAt === null) {
      markTokenRefreshed(now);
      return;
    }
    if (now - refreshedAt < TOKEN_KEEPALIVE_MS) return;
    if (now - lastKeepaliveAttemptRef.current < KEEPALIVE_RETRY_MS) return;
    lastKeepaliveAttemptRef.current = now;

    refreshAccessToken().catch((error) => {
      // The server no longer accepts the refresh token: the session is gone.
      if (error?.response?.status === 401) expireSession();
    });
  };

  useEffect(() => {
    const recordActivity = () => {
      if (!isLoggedIn()) return;
      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWriteRef.current = now;
      markActivity(now);
      keepTokenAlive(now);
    };

    const checkIdle = () => {
      if (!isLoggedIn()) return;
      // Sessions started before this timer existed have no timestamp yet.
      if (getLastActivity() === null) {
        markActivity();
        return;
      }
      if (getRemainingMs() === 0) {
        expireSession();
      }
    };

    // Check before listening, so returning to a tab or reopening the browser
    // after the timeout logs out instead of counting the reload as activity.
    checkIdle();

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, recordActivity, { capture: true, passive: true }),
    );
    document.addEventListener('visibilitychange', checkIdle);
    const intervalId = setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, recordActivity, { capture: true }),
      );
      document.removeEventListener('visibilitychange', checkIdle);
      clearInterval(intervalId);
    };
  }, []);

  // Switching pages counts as activity; the first render is just the page load.
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (!isLoggedIn()) return;
    const now = Date.now();
    markActivity(now);
    keepTokenAlive(now);
  }, [pathname]);

  return null;
};

export default SessionTimeout;
